import React, { useCallback, useState, useMemo } from 'react';
import Button from '@material-ui/core/Button';
import TreeView from '@material-ui/lab/TreeView';

import { StructMapCtx } from './struct-map-ctx';
import { useStyles } from './struct-map-styles';
import { getAllNodesIds } from './struct-map-utils';
import {
  StructMapProps,
  StructMapCtxProps,
  StructMapData,
  StructMapType,
} from './struct-map-types';
import { StructMapItem } from './struct-map-item';
import {
  BulkActions as DefaultBulkActions,
  NodeActions as DefaultNodeActions,
  LeafActions as DefaultLeafActions,
} from './default/actions';
import {
  nodeLabelMapper as defaultNodeLabelMapper,
  leafLabelMapper as defaultLeafLabelMapper,
} from './default/label-mapper';
import {
  nodeTooltipMapper as defaultNodeTooltipMapper,
  leafTooltipMapper as defaulLeaftTooltipMapper,
} from './default/tooltip-mapper';

export function StructMap({
  data,
  onChange,
  onLeafClick,
  type = StructMapType.DEFAULT,
  BulkActions = DefaultBulkActions,
  NodeActions = DefaultNodeActions,
  nodeLabelMapper = defaultNodeLabelMapper,
  nodeTooltipMapper = defaultNodeTooltipMapper,
  LeafActions = DefaultLeafActions,
  leafLabelMapper = defaultLeafLabelMapper,
  leafTooltipMapper = defaulLeaftTooltipMapper,
  defaultExpandedAll = false,
  allowSelect = false,
  allowSpecialSelect = false,
  allowDrag = false,
  allowDrop = false,
  allowAdd = false,
  allowEdit = false,
  allowDelete = false,
  outterCtxProps = {},
  missingNodes = [],
  extraNodes = [],
  parentEntity,
}: StructMapProps) {
  const classes = useStyles();

  /**
   * Array of ids of currently expanded nodes.
   */
  const [expandedNodes, setExpandedNodes] = useState<string[]>(
    defaultExpandedAll ? getAllNodesIds(data) : []
  );

  /**
   * Array of ids of currently selected leaves.
   */
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);

  /**
   * Array of ids of currently special selected leaves.
   */
  const [specialSelectedNodes, setSpecialSelectedNodes] = useState<string[]>(
    []
  );

  /**
   * Array of ids of currently editing nodes.
   */
  const [editingNodes, setEditingNodes] = useState<string[]>([]);

  /**
   * Array of currently dragged nodes.
   */
  const [draggedNodes, setDraggedNodes] = useState<StructMapData[]>([]);

  /**
   * Handlers.
   */
  const handleCollapseAll = useCallback(() => {
    setExpandedNodes([]);
  }, []);

  const handleExpandAll = useCallback(() => {
    const allNodes = getAllNodesIds(data);
    setExpandedNodes(allNodes);
  }, [data]);

  const handleToggle = useCallback(
    (event: React.ChangeEvent<{}>, nodeIds: string[]) => {
      setExpandedNodes(nodeIds);
    },
    []
  );

  /**
   * Context.
   */
  const ctx: StructMapCtxProps = useMemo(
    () => ({
      allData: data,
      onChange,
      onLeafClick,
      expandedNodes,
      allowSelect,
      allowSpecialSelect,
      allowDrag,
      allowDrop,
      allowAdd,
      allowEdit,
      allowDelete,
      selectedNodes,
      setSelectedNodes,
      specialSelectedNodes,
      setSpecialSelectedNodes,
      editingNodes,
      setEditingNodes,
      classes: classes,
      type,
      nodeLabelMapper,
      nodeTooltipMapper,
      NodeActions,
      leafLabelMapper,
      leafTooltipMapper,
      LeafActions,
      draggedNodes,
      setDraggedNodes,
      missingNodes,
      extraNodes,
      handleExpandAll,
      handleCollapseAll,
      handleToggle,
      parentEntity,
      ...outterCtxProps,
    }),
    [
      data,
      onChange,
      onLeafClick,
      expandedNodes,
      allowSelect,
      allowSpecialSelect,
      allowDrag,
      allowDrop,
      allowAdd,
      allowEdit,
      allowDelete,
      selectedNodes,
      specialSelectedNodes,
      editingNodes,
      classes,
      type,
      nodeLabelMapper,
      nodeTooltipMapper,
      NodeActions,
      leafLabelMapper,
      leafTooltipMapper,
      LeafActions,
      draggedNodes,
      missingNodes,
      extraNodes,
      handleExpandAll,
      handleCollapseAll,
      handleToggle,
      parentEntity,
      outterCtxProps,
    ]
  );

  return (
    <StructMapCtx.Provider value={ctx}>
      <div style={{ width: '100%' }}>
        <Button
          size="small"
          classes={{ root: classes.actionButton }}
          variant="contained"
          onClick={handleCollapseAll}
          disableElevation={true}
        >
          Sbalit vše
        </Button>
        <Button
          size="small"
          classes={{ root: classes.actionButton }}
          variant="contained"
          onClick={handleExpandAll}
          disableElevation={true}
        >
          Rozbalit vše
        </Button>

        <BulkActions
          allData={ctx.allData}
          classes={ctx.classes}
          selected={ctx.selectedNodes}
          specialSelected={ctx.specialSelectedNodes}
        />

        <TreeView
          className={ctx.classes.root}
          expanded={ctx.expandedNodes}
          onNodeToggle={ctx.handleToggle}
        >
          <StructMapItem archivalEntity={ctx.parentEntity} data={ctx.allData} />
        </TreeView>
      </div>
    </StructMapCtx.Provider>
  );
}
